import React from "react";
import * as ReactDOM from "react-dom";
import { EmptyState, Icon, notify, confirmDialog, RoiPanel } from "./registry";
import { ROLES, roleLabel, storeCan } from "./lib/roles";
import { NotificationsPanel } from "./pushsettings";   // phone + desktop notifications (lib/push.ts)
import { PACKS, SEAT_PRICE, fmtCredits, pctUsed } from "./lib/credits";   // credit model (publishes window.VFP_CREDITS)
import { planLimit, quoteLimit, limitStatus, monthBounds, estimatesThisMonth, ownProductCount, activeUsers } from "./lib/limits";
/* ============================================================
   Fence Works — Account, Billing, Plans, feature gating
   ============================================================ */
const FOUNDER_OFF = 0.5;                 // 50% off founder pricing (Stripe coupon FOUNDERS50)
// Founder-pricing deadline — ONE place in the app; keep in sync with the
// marketing site (site/data.js → FW.SITE.promo). Copy that mentions the date
// reads these instead of hardcoding it.
const FOUNDER_ENDS = "2026-11-30";
const FOUNDER_ENDS_LABEL = "Nov 30, 2026";
const founderActive = () => new Date() <= new Date(FOUNDER_ENDS + "T23:59:59");
const founder = (full) => Math.round(full * (1 - FOUNDER_OFF));

function printReceipt(iv, settings, owner) {
  const co = (settings && settings.companyName) || "Fence Works";
  const w = window.open("", "_blank", "width=620,height=800");
  if (!w) return;
  w.document.write(`<!doctype html><html><head><title>Receipt — ${iv.date}</title><style>
    body{font-family:-apple-system,system-ui,sans-serif;color:#111;margin:0;padding:40px;}
    .r{max-width:520px;margin:0 auto}h1{font-size:20px;margin:0}.muted{color:#666;font-size:13px}
    .row{display:flex;justify-content:space-between;padding:10px 0;border-bottom:1px solid #eee}
    .tot{font-weight:800;font-size:18px;border-bottom:0;padding-top:16px}
    .badge{display:inline-block;background:#e6f7ef;color:#0a7a4f;font-size:12px;font-weight:700;padding:3px 10px;border-radius:99px}
  </style></head><body><div class="r">
    <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px">
      <div><h1>${co}</h1><div class="muted">Fence Works subscription</div></div>
      <div style="text-align:right"><div class="muted">RECEIPT</div><div>${iv.date}</div><span class="badge">${iv.status}</span></div>
    </div>
    <div class="row"><span>${iv.desc}</span><span>$${iv.amount}.00</span></div>
    <div class="row"><span class="muted">Billed to</span><span>${owner || ""}</span></div>
    <div class="row tot"><span>Total paid</span><span>$${iv.amount}.00</span></div>
    <p class="muted" style="margin-top:24px">Paid via Stripe · Founder pricing (50% off). Thank you!</p>
  </div><script>setTimeout(()=>window.print(),250)<\/script></body></html>`);
  w.document.close();
}

// `credits`/`voiceMin` mirror lib/credits ALLOW (single source of truth for the model;
// these numeric keys let the UI read allowances without importing the map everywhere).
// caps.sms / caps.renders are booleans so window.VFP_PLAN.allows keeps working.
const PLANS = [
  { id: "free", name: "Free", full: 0, tagline: "Try the estimator", seats: 1, credits: 0, voiceMin: 0,
    features: ["1 user", "Estimate & quote up to 5 jobs/mo", "Satellite estimator", "PDF quotes (Fenceworks branding)", "Request-an-estimate form", "Lead list + CSV export"],
    caps: { estimates: 5, quotes: 5, products: 3, users: 1, payments: false, esign: false, branded: false, workorders: false, integrations: false, widget: false, inventory: false, whitelabel: false, sms: false, renders: false, website: false } },
  { id: "starter", name: "Starter", full: 79, tagline: "Sell the job", seats: 1, credits: 250, voiceMin: 0,
    features: ["1 user", "Unlimited estimates & quotes", "Branded quotes, e-signatures & deposits", "Homeowner self-quote widget", "3D Fence Visualizer · unlimited", "250 monthly credits"],
    caps: { estimates: Infinity, quotes: Infinity, products: 10, users: 1, payments: true, esign: true, branded: true, workorders: false, integrations: false, widget: true, inventory: false, whitelabel: false, sms: false, renders: true, website: false } },
  { id: "pro", name: "Pro", full: 149, tagline: "Most popular", popular: true, seats: 3, credits: 1000, voiceMin: 90,
    features: ["3 users", "Work orders, scheduling & Crew Mode", "Follow-ups, reviews & referrals", "Customer Hub + financing", "QuickBooks, Stripe & Google Calendar", "AI Call Team + texting · 90 talk-minutes (assisted activation)", "1,000 monthly credits"],
    caps: { estimates: Infinity, quotes: Infinity, products: Infinity, users: 3, payments: true, esign: true, branded: true, workorders: true, integrations: true, widget: true, inventory: false, whitelabel: false, sms: true, renders: true, website: false } },
  { id: "growth", name: "Growth", full: 249, tagline: "Scale the operation", seats: 15, credits: 6000, voiceMin: 800,
    features: ["15 users", "Your website — built, hosted & kept fresh on your own domain", "Multi-crew dispatch", "Inventory & purchase orders", "White-label quotes & widget", "3D yard previews for website visitors", "Unlimited Lead Radar claims", "AI Call Team · 800 talk-minutes; extra numbers billed separately", "Priority support", "6,000 monthly credits"],
    caps: { estimates: Infinity, quotes: Infinity, products: Infinity, users: 15, payments: true, esign: true, branded: true, workorders: true, integrations: true, widget: true, inventory: true, whitelabel: true, sms: true, renders: true, website: true } },
];
// accounts that subscribed under the retired Enterprise name are Growth now
const PLAN_ALIAS = { enterprise: "growth" };
const PLAN = (id) => PLANS.find(p => p.id === (PLAN_ALIAS[id] || id)) || PLANS[0];
window.VFP_PLAN = { get: (store) => PLAN((store && store.settings && store.settings.plan) || "free"),
  allows: (store, cap) => !!PLAN((store && store.settings && store.settings.plan) || "free").caps[cap] };
const planRank = (id) => PLANS.findIndex(p => p.id === PLAN(id).id);

/* ---- counted caps (lib/limits.ts) on screen ----
   Read off the same plan as the route gates. productRoom / estimateRoom say
   what's used; LimitNote is the "3 of 3 products on Free" line under a
   screen's title; limitPrompt is the one upgrade ask a blocked "new" shows. */
const planOf = (store: any) => PLAN((store && store.settings && store.settings.plan) || "free");
function productRoom(store: any) {
  const plan = planOf(store);
  return { plan, ...limitStatus(ownProductCount(store && store.customProducts), planLimit(plan, "products")) };
}
function estimateRoom(store: any, now = Date.now()) {
  const plan = planOf(store);
  return { plan, ...limitStatus(estimatesThisMonth(store && store.quotes, now), quoteLimit(plan)) };
}
const LIMITS: any = {
  products: { room: productRoom, cap: (p: any) => planLimit(p, "products"), noun: "products",
    over: (r: any) => `${r.plan.name} holds ${r.limit} products — you have ${r.used}`,
    keep: "Every product you already have keeps working and stays editable." },
  estimates: { room: estimateRoom, cap: quoteLimit, noun: "estimates this month",
    over: (r: any) => `${r.plan.name} includes ${r.limit} estimates a month — you've made ${r.used}`,
    keep: "Estimates you've already made stay fully editable and sendable." },
};
const limitResetDay = () => new Date(monthBounds(Date.now()).end).toLocaleDateString(undefined, { month: "short", day: "numeric" });
// "Starter holds 10; Pro and up have no limit"
function upgradeLine(kind: string, plan: any) {
  const cap = LIMITS[kind].cap, top = PLANS[PLANS.length - 1], out: string[] = [];
  for (const p of PLANS.filter(x => planRank(x.id) > planRank(plan.id) && cap(x) > cap(plan))) {
    if (Number.isFinite(cap(p))) { out.push(`${p.name} holds ${cap(p)}`); continue; }
    out.push(p.id === top.id ? `${p.name} has no limit` : `${p.name} and up have no limit`);
    break;
  }
  return out.join("; ");
}
async function limitPrompt(store: any, go: any, kind: "products" | "estimates") {
  const L = LIMITS[kind], r = L.room(store);
  // over the cap happens after a downgrade: say it plainly rather than "5 of 3"
  const title = r.over ? L.over(r) : `You've used ${r.used} of ${r.limit} ${L.noun} on ${r.plan.name}`;
  const up = upgradeLine(kind, r.plan);
  // the plan is the owner's to change — anyone else is told who to ask
  if (!storeCan(store, "billing")) { notify(`${title}. Ask the account owner to upgrade for more.`, { kind: "info", ms: 7000 }); return; }
  const body = [up ? up + "." : "", L.keep, kind === "estimates" ? `The count starts over on ${limitResetDay()}.` : ""].filter(Boolean).join(" ");
  if (await confirmDialog({ title, body, confirmLabel: "See plans", cancelLabel: "Not now" })) go("account", { tab: "plan" });
}
/* the usage line under a screen's title; nothing when the plan has no limit */
function LimitNote({ store, go, kind }: any) {
  const L = LIMITS[kind], r = L.room(store);
  if (!Number.isFinite(r.limit)) return null;
  return (
    <span className="muted" style={{ display: "block", fontSize: 12.5, marginTop: 6 }}
      title={kind === "estimates" ? `Estimates created since the 1st — starts over on ${limitResetDay()}` : "Your own products in the price book"}>
      <b className="tnum" style={{ color: r.atLimit ? "var(--amber)" : "var(--fg-2)" }}>{r.used} of {r.limit}</b> {L.noun} on {r.plan.name}
      {r.atLimit && storeCan(store, "billing") && <> · <a style={{ color: "var(--teal)", cursor: "pointer" }} onClick={() => go("account", { tab: "plan" })}>upgrade for more</a></>}
    </span>
  );
}

/* What switching a LIVE subscription does, said before it happens. It has to
   match server/src/billing.js: every switch is live at once, Stripe prorates it
   onto the NEXT invoice (nothing is charged on the spot), and a trial pays
   nothing until it ends. Downgrades are NOT held to the end of the period —
   the old copy said they were, and the code never did that. */
function planChangeCopy(fromId, toId, trialing) {
  const from = PLAN(fromId), to = PLAN(toId);
  if (trialing) return {
    title: `Switch your trial to ${to.name}?`,
    body: `${to.name} starts now and your trial keeps its end date. Nothing is charged today; when the trial ends, your first charge is the ${to.name} price.`,
    confirmLabel: `Switch to ${to.name}`,
  };
  if (planRank(toId) > planRank(fromId)) return {
    title: `Upgrade to ${to.name} now?`,
    body: `${to.name} starts right away. Nothing is charged today: your next invoice adds the difference for the rest of this billing period, then ${to.name} bills monthly. Add-on seats that ${to.name} already includes stop being billed.`,
    confirmLabel: `Upgrade to ${to.name}`,
  };
  return {
    title: `Switch to ${to.name} now?`,
    body: `This takes effect right away, not at the end of the billing period: anything only ${from.name} includes switches off today. The unused part of what you paid for ${from.name} comes off your next invoice as a credit, then ${to.name} bills monthly. Add-on seats you bought stay.`,
    confirmLabel: `Switch to ${to.name}`,
  };
}

/* Add-on seats — $5/seat/mo on top of the plan's included seats. Live mode
   pushes the new TOTAL seat count to Stripe (or opens the portal as a fallback);
   demo mode just tracks extraSeats locally so the UI reflects the new count.
   `minExtra` is the fewest add-ons that still seat everyone on the team — the
   server refuses anything lower, so the − stops there too. */
function SeatStepper({ store, baseSeats, extra, onChange, busy, minExtra = 0 }: any) {
  const set = (n: number) => onChange(Math.max(0, n));
  return (
    <div className="row gap-8" style={{ alignItems: "center", flexWrap: "wrap" }}>
      <span className="field-label" style={{ margin: 0 }}>Add seats</span>
      <div className="row gap-8 seat-step" style={{ alignItems: "center" }}>
        <button className="btn btn-ghost btn-sm" disabled={busy || extra <= 0 || extra <= minExtra} onClick={() => set(extra - 1)} aria-label="Remove a seat" title={extra > 0 && extra <= minExtra ? "Every seat is in use — remove a teammate or invite first" : undefined}>−</button>
        <b className="tnum" style={{ fontSize: 15, minWidth: 18, textAlign: "center" }}>{extra}</b>
        <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => set(extra + 1)} aria-label="Add a seat">+</button>
      </div>
      <span className="muted" style={{ fontSize: 12.5 }}>$5/mo each</span>
      {extra > 0 && <span className="chip teal" style={{ fontSize: 10.5 }}>+{extra} seat{extra > 1 ? "s" : ""} · ${extra * SEAT_PRICE}/mo</span>}
      <span className="faint" style={{ fontSize: 11.5 }}>{baseSeats + extra} seats total</span>
      {busy && <span className="muted" style={{ fontSize: 11.5 }}>saving…</span>}
    </div>
  );
}

/* Team — multi-seat workspaces. Owner invites teammates by email; they accept
   a link and join this workspace. Seats are plan-gated server-side. */
function TeamPanel({ store, go }: any) {
  const DB: any = window.VFP_DB;
  const live = DB && DB.teamMembers && DB.apiBase && DB.apiBase();
  const plan = PLAN((store.settings && store.settings.plan) || "free");
  const [data, setData] = React.useState<any>(null);
  const [email, setEmail] = React.useState<any>("");
  const [busy, setBusy] = React.useState<any>(false);
  const [seatBusy, setSeatBusy] = React.useState<any>(false);
  const [err, setErr] = React.useState<any>("");
  const [seatErr, setSeatErr] = React.useState<any>("");   // a refused seat change, shown on the add-on card
  const [link, setLink] = React.useState<any>("");
  const [role, setRole] = React.useState<any>("crew");   // pick permissions BEFORE the link is sent — least access by default
  const extraSeats = Number((store.settings && store.settings.extraSeats) || 0);
  const load = () => { if (live) DB.teamMembers().then(setData).catch((e: any) => setErr(String(e.message || e))); };
  React.useEffect(() => { load(); }, []);
  // seats in use: the server's count, else members + live invites (lib/limits)
  const seatRoom = () => limitStatus(data ? (data.used != null ? data.used : activeUsers(data, Date.now())) : 0, effSeats || 1);
  async function invite() {
    setErr(""); setLink(""); const e = email.trim(); if (!e) return;
    // the server refuses too (409 "seats"); say it before the round trip
    if (seatRoom().atLimit) { setErr(`All ${effSeats} seat${effSeats === 1 ? "" : "s"} are taken by your team and pending invites. Add a seat or upgrade to invite someone new.`); return; }
    setBusy(true);
    try { const r = await DB.teamInvite(e, role); setEmail(""); setLink(r.link || ""); load(); }
    catch (ex: any) { setErr(String(ex.message || ex)); } finally { setBusy(false); }
  }
  async function remove(target: any, label: string) {
    if (!(await confirmDialog({ title: `Remove this ${label}?`, confirmLabel: "Remove", danger: true }))) return;
    try { await DB.teamRemove(target); load(); } catch (ex: any) { setErr(String(ex.message || ex)); }
  }
  // Live: server's data.seats is authoritative (it already folds in purchased seats).
  // Demo: effective seats = plan's included seats + locally-tracked add-ons.
  const baseSeats = live ? ((data && data.seats) || plan.seats || 1) : (plan.seats || 1);
  const effSeats = live ? baseSeats : baseSeats + extraSeats;
  // the plan's own seats as the SERVER counts them (from the price being paid);
  // the local plan can lag a switch, and a total built on it moved the add-ons
  const included = live ? ((data && data.included) || plan.seats || 1) : (plan.seats || 1);
  // Demo add-on stepper edits extraSeats directly; live pushes the new TOTAL.
  async function changeSeats(nextExtra: number) {
    setSeatErr("");
    if (!live) { store.setSettings({ extraSeats: nextExtra }); store.addActivity(`Team seats set to ${(plan.seats || 1) + nextExtra} (+${nextExtra} add-on)`); return; }
    const total = included + nextExtra;
    setSeatBusy(true);
    try {
      if (DB.billingUpdateSeats) { await DB.billingUpdateSeats(total); load(); }            // ASSUMED endpoint — guarded
      else { const r = await DB.billingPortal(); if (r && r.url) { window.location.href = r.url; return; } }
    } catch (ex: any) { setSeatErr(String(ex.message || ex)); } finally { setSeatBusy(false); }
  }

  if (!live) {
    // Demo: no team service, but the seat add-on still works locally so the model is testable.
    return (
      <div style={{ display: "grid", gap: 16 }}>
        <div className="card pad">
          <div className="row between" style={{ marginBottom: 10, flexWrap: "wrap", gap: 6 }}>
            <span className="row gap-8 kicker kicker-teal"><Icon name="user" size={14} />Your team</span>
            <span className="muted" style={{ fontSize: 12.5 }}>1 of {effSeats} seat{effSeats > 1 ? "s" : ""} used · {plan.name} plan</span>
          </div>
          <SeatStepper store={store} baseSeats={plan.seats || 1} extra={extraSeats} onChange={changeSeats} busy={seatBusy} />
          <p className="faint" style={{ fontSize: 11.5, marginTop: 10, margin: "10px 0 0" }}>Inviting teammates needs the live service (app.fenceworks.ai). Add-on seats are ${SEAT_PRICE}/mo each and apply the moment you upgrade.</p>
        </div>
      </div>
    );
  }
  if (!data) return <div className="card pad"><p className="muted" style={{ margin: 0 }}>Loading team…</p></div>;
  const seatsLeft = seatRoom().left;
  // purchased add-on seats, from the server's seat line (NOT the local demo
  // counter) so the stepper reflects reality after a change
  const liveExtra = data.addon != null ? Number(data.addon) || 0 : Math.max(0, (effSeats || included) - included);
  const minExtra = Math.max(0, (data.used || 0) - included);
  const me = (data.members || []).find((m: any) => m.id === data.you);
  // owner/admin (or a legacy null-role solo owner) may invite & remove
  const canManage = !me || !me.role || me.role === "owner" || me.role === "admin";
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div className="card pad">
        <div className="row between" style={{ marginBottom: 12, flexWrap: "wrap", gap: 6 }}>
          <span className="row gap-8 kicker kicker-teal"><Icon name="user" size={14} />Your team</span>
          <span className="muted" style={{ fontSize: 12.5 }}>{data.used} of {effSeats} seat{effSeats > 1 ? "s" : ""} used · {data.plan} plan</span>
        </div>
        <div style={{ display: "grid", gap: 2 }}>
          {(data.members || []).map((m: any) => (
            <div key={m.id} className="row between" style={{ padding: "9px 0", borderBottom: "1px solid var(--line)" }}>
              <div style={{ minWidth: 0 }}><b style={{ fontSize: 13.5 }}>{m.full_name || m.email}</b>{m.id === data.you && <span className="chip" style={{ fontSize: 9.5, marginLeft: 6 }}>you</span>}<div className="muted" style={{ fontSize: 12 }}>{m.email} · {roleLabel(m.role)}</div></div>
              {m.id !== data.you && canManage && <button className="btn btn-ghost btn-sm" title="Remove" onClick={() => remove({ userId: m.id }, "member")}><Icon name="trash" size={13} /></button>}
            </div>
          ))}
          {(data.invites || []).map((i: any) => (
            <div key={i.id} className="row between" style={{ padding: "9px 0", borderBottom: "1px solid var(--line)", opacity: 0.8 }}>
              {/* an expired invite can't be accepted, so it no longer holds a seat */}
              <div style={{ minWidth: 0 }}><b style={{ fontSize: 13.5 }}>{i.email}</b><div className="muted" style={{ fontSize: 12 }}>{i.expires_at && new Date(i.expires_at).getTime() <= Date.now() ? "Invite expired · not using a seat" : "Invite pending"} · {roleLabel(i.role)}</div></div>
              {canManage && <button className="btn btn-ghost btn-sm" onClick={() => remove({ inviteId: i.id }, "invite")}>Revoke</button>}
            </div>
          ))}
        </div>
      </div>
      {canManage && <div className="card pad">
        <div className="row between" style={{ marginBottom: 10, flexWrap: "wrap", gap: 6 }}>
          <span className="kicker" style={{ margin: 0 }}>Add-on seats</span>
        </div>
        <SeatStepper store={store} baseSeats={included} extra={liveExtra} minExtra={minExtra} onChange={changeSeats} busy={seatBusy} />
        <p className="faint" style={{ fontSize: 11.5, margin: "10px 0 0" }}>
          {included} seat{included === 1 ? "" : "s"} come with your plan. Add-on seats are ${SEAT_PRICE}/mo each and change right away: an added seat puts the prorated amount for the rest of this billing period on your next invoice, and a removed one credits the unused part. You can't go below the people on your team plus pending invites.
        </p>
        {seatErr && <p style={{ color: "var(--amber)", fontSize: 12, margin: "8px 0 0" }}>{seatErr}</p>}
      </div>}
      {canManage && <div className="card pad">
        <div className="kicker" style={{ marginBottom: 10 }}>Invite a teammate</div>
        {seatsLeft > 0 ? (
          <>
            <div className="row gap-8" style={{ flexWrap: "wrap" }}>
              <input className="input" style={{ flex: 1, minWidth: 200 }} type="email" value={email} placeholder="teammate@email.com" onChange={e => setEmail(e.target.value)} onKeyDown={e => { if (e.key === "Enter") invite(); }} />
              <select className="input" value={role} style={{ width: 150 }} onChange={e => setRole(e.target.value)} title="Set their permissions before the invite goes out">
                {ROLES.filter(r => r.id !== "owner").map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
              <button className="btn btn-primary" disabled={busy || !email.trim()} onClick={invite}>{busy ? "Sending…" : "Send invite"}</button>
            </div>
            <p className="faint" style={{ fontSize: 11.5, marginTop: 8 }}>
              {(ROLES.find(r => r.id === role) || ROLES[1]).blurb}
              {" "}Their invite email is locked to the account they create.</p>
          </>
        ) : (
          <div className="row between" style={{ flexWrap: "wrap", gap: 8 }}>
            <span className="muted" style={{ fontSize: 13 }}>All {effSeats} seat{effSeats > 1 ? "s" : ""} used. Add a seat for ${SEAT_PRICE}/mo, or upgrade for more.</span>
            <div className="row gap-8">
              {/* liveExtra, not the demo counter: from 2 add-ons, "add one" used to set 1 */}
              <button className="btn btn-primary btn-sm" disabled={seatBusy} onClick={() => changeSeats(liveExtra + 1)}>{seatBusy ? "…" : `Add a seat · $${SEAT_PRICE}/mo`}</button>
              <button className="btn btn-outline btn-sm" onClick={() => go("account", { tab: "plan" })}>See plans</button>
            </div>
          </div>
        )}
        {link && <p className="faint" style={{ fontSize: 11.5, marginTop: 8, wordBreak: "break-all" }}>Invite link: <span style={{ color: "var(--teal)" }}>{link}</span></p>}
        {err && <p style={{ color: "var(--amber)", fontSize: 12, marginTop: 8 }}>{err}</p>}
      </div>}
    </div>
  );
}

/* FW-029 — deleting your account is a request, not a button.
   There used to be a client method pointing at a route that wiped the
   workspace synchronously: every quote, every signed contract, every customer,
   one call away and unrecoverable. It also wiped the identifiers before
   anything released the resources, so the Twilio number stayed rented and the
   A2P campaign kept billing with nothing left naming them.

   So: you ask, we confirm, and the purge releases the paid provider resources
   before it wipes anything. You can cancel while it's open — which is the
   recovery a destructive button never had. */
function DeleteAccountCard({ store }: any) {
  const DB: any = (window as any).VFP_DB;
  const live = !!(DB && DB.apiBase && DB.apiBase());
  const [open, setOpen] = React.useState(false);
  const [reason, setReason] = React.useState("");
  const [req, setReq] = React.useState<any>(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  React.useEffect(() => {
    if (!live || !DB.accountDeletionRequest) return;
    let alive = true;
    DB.accountDeletionRequest().then((d: any) => { if (alive && d) setReq(d.request || null); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  async function submit() {
    setBusy(true); setErr("");
    try {
      const d = await DB.requestAccountDeletion(reason);
      setReq(d.request || { created_at: Date.now() });
      setOpen(false); setReason("");
      notify("Request sent. Nothing has been deleted — we'll email you to confirm.", { kind: "success" });
    } catch (e: any) { setErr(String((e && e.message) || e)); }
    finally { setBusy(false); }
  }
  async function cancel() {
    if (!(await confirmDialog({ title: "Keep your account?", body: "We'll stop the deletion request. Nothing was removed.", confirmLabel: "Keep it" }))) return;
    setBusy(true); setErr("");
    try { await DB.cancelAccountDeletion(); setReq(null); notify("Deletion request cancelled — your account stays as it is.", { kind: "success" }); }
    catch (e: any) { setErr(String((e && e.message) || e)); }
    finally { setBusy(false); }
  }

  if (!live) return null;
  return (
    <div className="card pad" style={{ maxWidth: 520, marginBottom: 14, borderColor: req ? "var(--amber)" : undefined }}>
      <div className="kicker" style={{ marginBottom: 8 }}>Delete this account</div>
      {req ? (
        <>
          <p className="muted" style={{ fontSize: 13, margin: "0 0 10px", lineHeight: 1.5 }}>
            You asked us to delete this account{req.created_at ? " on " + new Date(req.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric" }) : ""}.
            <b> Nothing has been removed yet.</b> We'll email {req.requester_email || "you"} to confirm before anything is deleted — usually within a business day.
          </p>
          <button className="btn btn-ghost btn-sm" disabled={busy} onClick={cancel}>Changed my mind — keep my account</button>
        </>
      ) : !open ? (
        <>
          <p className="muted" style={{ fontSize: 13, margin: "0 0 10px", lineHeight: 1.5 }}>
            This erases everything: every quote, signed contract, customer, job and photo, plus your team's logins. It cannot be undone, so we confirm it with you first rather than doing it on a tap.
          </p>
          <button className="btn btn-sm" style={{ background: "var(--red-tint)", color: "var(--red)", border: "1px solid var(--red-tint)" }} onClick={() => setOpen(true)}>Request account deletion</button>
        </>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          <p className="muted" style={{ fontSize: 13, margin: 0, lineHeight: 1.5 }}>
            We'll email you to confirm, then permanently erase this workspace and close down your texting number and AI receptionist so nothing keeps billing. Anything we can tell you first?
          </p>
          <textarea className="textarea" rows={3} placeholder="What made you decide to go? (optional)" value={reason} onChange={e => setReason(e.target.value)} />
          <div className="row gap-8">
            <button className="btn btn-sm" style={{ background: "var(--red-tint)", color: "var(--red)", border: "1px solid var(--red-tint)" }} disabled={busy} onClick={submit}>{busy ? "Sending…" : "Send the request"}</button>
            <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => { setOpen(false); setErr(""); }}>Never mind</button>
          </div>
        </div>
      )}
      {err && <p style={{ color: "var(--amber)", fontSize: 12.5, margin: "8px 0 0" }}>{err}</p>}
    </div>
  );
}

function AccountBilling({ store, go, auth, params }: any) {
  const checkout = params && params.checkout;   // "success" | "cancel" when returning from Stripe
  const s = store.settings || {};
  const planId = s.plan || "free";
  const plan = PLAN(planId);
  const user = (auth && auth.user) || null;
  const name = user ? user.name : (s.companyOwner || "Your account");
  const email = user ? user.email : (s.ownerEmail || "");
  // an admin has no Plan tab, so they open on Team rather than a blank panel
  const [tab, setTab] = React.useState<any>((params && params.tab) || (storeCan(store, "billing") ? "plan" : "team"));
  React.useEffect(() => { if (params && params.tab) setTab(params.tab); }, [params && params.tab]);
  const [cancelling, setCancelling] = React.useState<any>(false);
  const [cancelStep, setCancelStep] = React.useState<any>("reason");   // "reason" → "followup" → "offer" cancel flow
  const [cancelReason, setCancelReason] = React.useState<any>("");      // selected churn reason (logged on proceed)
  const [cancelOther, setCancelOther] = React.useState<any>("");        // free-text on the reason step ("anything else?")
  const [cancelFollowup, setCancelFollowup] = React.useState<any>("");  // branched follow-up answer (per-reason option pick)
  const [cancelDetail, setCancelDetail] = React.useState<any>("");      // free-text follow-up (verbatim feature ask / "what's better" / other)
  const [extendBusy, setExtendBusy] = React.useState<any>(false);
  const [extendDone, setExtendDone] = React.useState<any>("");          // confirmation copy after a trial extension
  const [busy, setBusy] = React.useState<any>("");
  const [sub, setSub] = React.useState<any>(null);
  const [planNote, setPlanNote] = React.useState<any>("");   // brief success note after an in-place plan switch
  const [planErr, setPlanErr] = React.useState<any>("");     // a switch the server refused for seats ("Remove 2 team members first…")
  const [billingErr, setBillingErr] = React.useState<any>(false);   // billingStatus() threw — keep the current plan, say so quietly
  const DB = window.VFP_DB;
  const liveBilling = !!(DB && DB.apiBase && DB.apiBase() && DB.billingCheckout);

  // Pull the real (Stripe-backed) subscription and reflect its plan for gating.
  // Only a PAID active/trialing sub unlocks its plan — a canceled checkout or
  // lapsed sub means Free, no limbo. After a checkout success the webhook can
  // lag by a few seconds, so poll briefly until the row flips.
  React.useEffect(() => {
    if (!liveBilling) return;
    let stop = false;
    let tries = checkout === "success" ? 6 : 1;
    const paidOf = (x) => x && x.plan && x.plan !== "free" && (x.status === "active" || x.status === "trialing" || x.status === "past_due");   // past_due = still on, inside Stripe's retry window
    const tick = () => DB.billingStatus().then(r => {
      if (stop) return;
      setBillingErr(false);
      const subscription = (r && r.subscription) || null;
      setSub(subscription);
      // only a RESOLVED status may move the plan — a failed call (throws) must
      // never downgrade a paying customer to Free
      const plan = paidOf(subscription) ? subscription.plan : "free";
      if (plan !== ((store.settings && store.settings.plan) || "free")) store.setSettings({ plan });
      if (!paidOf(subscription) && --tries > 0) setTimeout(tick, 2000);
    }).catch(() => { if (!stop) setBillingErr(true); });
    tick();
    return () => { stop = true; };
  }, [liveBilling]);

  // true when the plan actually changed (the cancel flow records a downgrade
  // only then)
  async function choosePlan(id): Promise<boolean> {
    if (id === planId) return false;
    if (liveBilling) {
      setPlanErr("");
      // A live subscription switches in place, at once — say what that costs
      // before doing it. A new subscription goes to Stripe Checkout, which
      // shows the price itself.
      const trialing = !!(sub && sub.status === "trialing");
      const inPlace = !!(sub && sub.stripe_subscription_id && ["active", "trialing", "past_due"].includes(sub.status));
      if (inPlace && !(await confirmDialog(planChangeCopy(planId, id, trialing)))) return false;
      setBusy(id); setPlanNote("");
      try {
        const r: any = await DB.billingCheckout(id);     // → Stripe Checkout (new sub) OR { updated } (in-place switch)
        if (r && r.url) { window.location.href = r.url; return false; }   // brand-new subscription → Stripe Checkout
        // No url = the server switched the plan IN PLACE on the existing sub
        // (preserving any trial, prorating for payers). Reflect it now without a
        // redirect: optimistic plan, then reconcile against billingStatus().
        if (r && r.updated) {
          store.setSettings({ plan: id });
          try { const st = await DB.billingStatus(); setSub((st && st.subscription) || null); } catch (e) {}
          store.addActivity(`Plan changed to ${PLAN(id).name}`);
          setPlanNote(`Plan changed to ${PLAN(id).name}. ` + (trialing ? "Nothing is charged until your trial ends."
            : planRank(id) > planRank(planId) ? "The difference for the rest of this billing period is on your next invoice."
            : "The unused part of your old plan is credited on your next invoice."));
          return true;
        }
      } catch (e: any) {
        // seats the team is still using: say what to remove, and where
        if (e && e.code === "seats_in_use") setPlanErr(e.message);
        else notify("Couldn't start checkout: " + (e.message || e) + "\n\nIf Stripe isn't set up yet, add the plan price IDs on the server (see BILLING.md).");
      } finally { setBusy(""); }
      return false;
    }
    store.setSettings({ plan: id, planSince: Date.now() });   // demo fallback
    store.addActivity(id === "free" ? "Downgraded to Free"
      : `${planRank(id) > planRank(planId) ? "Upgraded" : "Switched"} to ${PLAN(id).name} — founder rate $${founder(PLAN(id).full)}/mo`);
    return true;
  }
  async function manageBilling() {
    if (liveBilling) {
      try { const r = await DB.billingPortal(); if (r && r.url) { window.location.href = r.url; return; } }
      catch (e) { notify("Billing portal unavailable: " + (e.message || e)); }
      return;
    }
    (store.settings.integrations && store.settings.integrations.stripe) ? go("integrations") : notify("Connect Stripe in Integrations to manage live billing.");
  }
  // Fire-and-forget the full branched survey to the central, anonymized churn
  // table on the TERMINAL action (best signal). Guarded — a feedback failure
  // (or no live service) must NEVER block the cancel/keep/extend/downgrade.
  // outcome ∈ "canceled" | "kept" | "extended_trial" | "downgraded".
  function submitFeedback(outcome: string) {
    try {
      const DBf: any = DB;
      if (DBf && DBf.cancelFeedback) {
        DBf.cancelFeedback({
          reason: cancelReason,
          followup: cancelFollowup,
          detail: cancelDetail || cancelOther,
          plan: planId,
          wasTrial: onTrial,
          outcome,
        });
      }
    } catch (e) {}
  }
  function applySaveOffer() {
    submitFeedback("kept");
    store.setSettings({ saveOfferUsed: true, freeMonthUntil: Date.now() + 30 * 864e5 });
    store.addActivity("Applied 1 free month — subscription retained");
    setCancelling(false);
  }
  // Comped by Fence Works (FW Admin → Customers, server/src/comps.js): the
  // service answers the comp's plan as active with comped: true. Nothing to
  // buy or cancel here; Stripe, if they also have a subscription, is untouched.
  const comped = !!(sub && sub.comped);
  const compUntil = comped && sub.comp && sub.comp.until
    ? new Date(sub.comp.until).toLocaleDateString("en-US", { timeZone: "UTC", month: "short", day: "numeric", year: new Date(sub.comp.until).getUTCFullYear() === new Date().getUTCFullYear() ? undefined : "numeric" })
    : "";
  const stripeStillBilling = comped && sub.stripe && sub.stripe.billing;
  // Trial-aware cancel: a Stripe trial (live) or the mirrored subStatus (demo).
  const onTrial = (sub && sub.status === "trialing") || s.subStatus === "trialing";
  // the next paid plan down, for the cancel flow's "downgrade instead" offer
  const cheaperPlan = planId === "growth" || planId === "enterprise" ? "pro" : planId === "pro" ? "starter" : null;
  // already-extended guard: demo flag, or Stripe says the trial was extended once
  const trialExtended = !!s.trialExtended || !!(sub && sub.data && sub.data.metadata && sub.data.metadata.trial_extended === "1");
  // open the cancel flow at step 1 (reason), clearing any prior selection/notes
  function openCancel() {
    setCancelStep("reason"); setCancelReason(""); setCancelOther(""); setCancelFollowup(""); setCancelDetail(""); setExtendDone(""); setCancelling(true);
  }
  // record the reason (churn signal) and advance to the tailored FOLLOW-UP step
  // (reason → followup → offer). The full survey is submitted on the terminal
  // action from the offer step, not here.
  function pickReason(reason: string) {
    setCancelReason(reason);
    setCancelFollowup(""); setCancelDetail("");   // fresh per-reason answers
    store.addActivity("Cancellation reason: " + reason + (reason === "Other" && cancelOther.trim() ? " — " + cancelOther.trim() : ""));
    setCancelStep("followup");
  }
  // Extend an in-progress trial by 14 days. Live: hit the server, confirm, refresh
  // status, then close. Demo: bump trialEndsAt locally + flag trialExtended.
  async function extendTrial() {
    if (trialExtended) return;   // one-time — UI also hides the button
    if (liveBilling && DB.billingExtendTrial) {
      setExtendBusy(true);
      try {
        const r = await DB.billingExtendTrial();
        const ends = r && r.trial_ends_at ? new Date(r.trial_ends_at).toLocaleDateString() : "";
        setExtendDone("Trial extended — now ends " + ends + " 🎉");
        try { const st = await DB.billingStatus(); setSub((st && st.subscription) || null); } catch (e) {}
        submitFeedback("extended_trial");
        store.addActivity("Extended trial 14 days");
        setTimeout(() => setCancelling(false), 1400);
      } catch (e: any) {
        notify("Couldn't extend your trial: " + ((e && e.message) || e));
      } finally { setExtendBusy(false); }
      return;
    }
    // demo fallback
    submitFeedback("extended_trial");
    store.setSettings({ trialEndsAt: Date.now() + 14 * 864e5, trialExtended: true });
    store.addActivity("Extended trial 14 days");
    setCancelling(false);
  }
  async function confirmCancel() {
    submitFeedback("canceled");   // fire BEFORE any portal redirect (fire-and-forget, never blocks)
    if (liveBilling) {   // cancellation happens in the Stripe Customer Portal
      try {
        const r = await DB.billingPortal();
        if (r && r.url) { window.location.href = r.url; return; }
        notify("Couldn't open the billing portal — try again in a moment.");
      }
      catch (e) { notify("Couldn't open the billing portal: " + (e.message || e)); }
      // the portal is the ONLY way a live sub gets canceled: never downgrade
      // locally when that call failed (the customer would still be billed)
      return;
    }
    store.setSettings({ plan: "free", canceledAt: Date.now() });
    store.addActivity("Canceled subscription — moved to Free");
    setCancelling(false);
  }
  // Usage & credits — live meter from the billing service (ASSUMED endpoint, guarded);
  // demo derives the monthly allowance from the plan and shows 0 used.
  // DBx casts past the typed surface for endpoints we feature-detect (assumed, optional).
  const DBx: any = DB;
  const [usage, setUsage] = React.useState<any>(null);
  const [packBusy, setPackBusy] = React.useState<any>("");
  const [creditsAdded, setCreditsAdded] = React.useState<any>(false);   // success note after a pack purchase reconciles
  React.useEffect(() => {
    if (!liveBilling || !DBx.billingUsage) { setUsage(null); return; }
    DBx.billingUsage().then((r: any) => setUsage(r || null)).catch(() => setUsage(null));
  }, [liveBilling, planId]);
  // Credit-pack RECONCILE on the success redirect — don't wait on the webhook
  // (it may not be configured / delivered, especially in Stripe test mode). The
  // server put the Checkout session id in success_url; the app router may not
  // surface `cs` as a param, so read it from the querystring directly. Verify +
  // credit on the server (idempotent), refresh the Usage meter, then strip the
  // query so a refresh can't re-run. All guarded — no live service → skip.
  React.useEffect(() => {
    if (checkout !== "credits") return;
    let sid: string | null = (params && params.cs) || null;
    try { if (!sid) sid = new URLSearchParams(window.location.search).get("cs"); } catch (e) {}
    if (!sid) return;
    if (!liveBilling || !DBx.billingCreditsConfirm) return;
    DBx.billingCreditsConfirm(sid)
      .then((r: any) => {
        if (r && r.ok) setCreditsAdded(true);
        if (DBx.billingUsage) return DBx.billingUsage().then((u: any) => setUsage(u || null));   // refresh the meter
      })
      .catch(() => {})
      .finally(() => {
        // drop ?checkout=credits&cs=… so a manual refresh doesn't re-confirm
        try { window.history.replaceState({}, "", window.location.pathname); } catch (e) {}
      });
  }, [checkout, liveBilling]);
  // local (demo) purchased balance (live balance comes from the usage snapshot)
  const creditBalance = Number(s.creditBalance || 0);
  // Fallback totals when there's no live server snapshot: derive from the credit
  // model, which applies the SAME 10% trial cap as enforcement. Using plan.credits
  // here would show a trial the FULL allowance (e.g. 0/1,000) while the server
  // caps at 100 — so prefer creditState(store) (trial-aware) over the raw plan.
  const VFP: any = (window as any).VFP_CREDITS;
  const cs = VFP && VFP.creditState ? VFP.creditState(store) : null;
  const fallbackCredits = cs ? cs.creditsTotal : (plan.credits || 0);
  const fallbackVoice = cs ? cs.minTotal : (plan.voiceMin || 0);
  const u = {   // normalized usage view (live → server [trial-capped], demo → trial-capped model)
    credits: { used: (usage && usage.credits && usage.credits.used) || 0, total: (usage && usage.credits && usage.credits.total) || fallbackCredits },
    voiceMin: { used: (usage && usage.voiceMin && usage.voiceMin.used) || 0, total: (usage && usage.voiceMin && usage.voiceMin.total) || fallbackVoice },
    balance: (usage && usage.balance != null) ? usage.balance : creditBalance,
  };
  async function buyCredits(pack: any) {
    if (liveBilling && DBx.billingBuyCredits) {
      setPackBusy(pack.id);
      try { const r = await DBx.billingBuyCredits(pack.id); if (r && r.url) { window.location.href = r.url; return; } }   // Stripe redirect
      catch (e: any) { notify("Couldn't start the credit purchase: " + (e.message || e)); }
      finally { setPackBusy(""); }
      return;
    }
    store.setSettings({ creditBalance: creditBalance + pack.credits });   // demo fallback
    store.addActivity(`Added ${fmtCredits(pack.credits)} credits`);
  }

  // billing history: real Stripe invoices when the service is live, else the demo rows
  const [liveInvoices, setLiveInvoices] = React.useState<any>(null);
  const [invoicesErr, setInvoicesErr] = React.useState<any>(false);
  const [invoicesTry, setInvoicesTry] = React.useState<any>(0);
  React.useEffect(() => {
    if (!liveBilling || !DB.billingInvoices) return;
    let dead = false;
    setInvoicesErr(false); setLiveInvoices(null);
    DB.billingInvoices().then((r: any) => { if (!dead) setLiveInvoices((r && r.invoices) || []); })
      .catch(() => { if (!dead) { setLiveInvoices([]); setInvoicesErr(true); } });
    return () => { dead = true; };
  }, [liveBilling, invoicesTry]);
  const invoices = liveBilling
    ? (liveInvoices || []).map((iv: any) => ({
        date: iv.date ? new Date(iv.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—",
        amount: Math.round(iv.amount), status: iv.status === "paid" ? "Paid" : (iv.status || ""), desc: iv.description || "Subscription", url: iv.url, pdf: iv.pdf,
      }))
    : (planId !== "free" ? [
        { date: "Jun 1, 2026", amount: founder(plan.full), status: "Paid", desc: plan.name + " — monthly (founder)" },
        { date: "May 1, 2026", amount: founder(plan.full), status: "Paid", desc: plan.name + " — monthly (founder)" },
      ] : []);

  return (
    <div className="page fade-up" style={{ maxWidth: 1040 }}>
      <h1 className="display" style={{ fontSize: 28, margin: "0 0 4px" }}>Account &amp; Billing</h1>
      <p className="muted" style={{ margin: "0 0 16px" }}>Change your plan, update payment, view invoices, or cancel — all self-serve, anytime.</p>

      {checkout === "cancel" && (
        <div className="card pad" style={{ marginBottom: 14, borderColor: "var(--amber)", display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
          <b style={{ color: "var(--amber)" }}>Checkout canceled — you weren't charged.</b>
          <span className="muted" style={{ fontSize: 13.5 }}>You're on the <b style={{ color: "var(--fg-2)" }}>Free plan</b> for now. Pick a plan below whenever you're ready — founder pricing still applies.</span>
        </div>
      )}
      {checkout === "success" && (
        <div className="card pad" style={{ marginBottom: 14, borderColor: "var(--green)", display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
          <b style={{ color: "var(--green)" }}>🎉 Subscription confirmed!</b>
          <span className="muted" style={{ fontSize: 13.5 }}>Your plan unlocks in a few seconds while Stripe finishes up. Founder pricing is locked in.</span>
        </div>
      )}
      {!comped && <div className="founder-banner"><span className="chip teal" style={{ fontSize: 10 }}>Founder pricing</span><span style={{ fontSize: 13 }}><b style={{ color: "var(--teal)" }}>50% off forever</b> — locked in for accounts before {FOUNDER_ENDS_LABEL}. Applied automatically at checkout.</span></div>}

      {/* the plan and its invoices are the owner's; an admin lands on Team */}
      <div className="seg wrap" role="tablist" style={{ margin: "16px 0 18px" }}>
        {[["plan", "Plan"], ["team", "Team"], ["notifications", "Notifications"], ["profile", "Profile"], ["billing", "Billing History"]]
          .filter(([id]) => storeCan(store, "billing") || id === "team" || id === "profile" || id === "notifications")
          .map(([id, lb]) =>
            <button key={id} role="tab" aria-selected={tab === id} className={"seg-btn" + (tab === id ? " on" : "")} onClick={() => setTab(id)}>{lb}</button>)}
      </div>

      {tab === "team" && <TeamPanel store={store} go={go} />}
      {tab === "notifications" && <NotificationsPanel store={store} go={go} />}

      {tab === "plan" && (
        <>
          <div className="card pad" style={{ marginBottom: 16 }}>
            <div className="row gap-10" style={{ marginBottom: 6 }}>
              <span className="plan-pill">{plan.name}</span>
              {(() => {
                const good = sub && (sub.status === "active" || sub.status === "trialing" || sub.status === "past_due") && sub.plan !== "free";
                const label = !sub ? (planId === "free" ? "Free forever" : "Active")
                  : comped ? "Complimentary" + (compUntil ? " until " + compUntil : "")
                  : sub.cancel_at_period_end ? "Canceling at period end"
                  : sub.status === "trialing" ? "Trial" + (sub.trial_ends_at ? " — ends " + new Date(sub.trial_ends_at).toLocaleDateString() : "")
                  : sub.status === "active" ? (sub.plan === "free" ? "Free forever" : "Active")
                  : sub.status === "past_due" ? "Past due — update payment"
                  : sub.status === "canceled" ? "Canceled"
                  : "Not subscribed";
                return <span style={{ color: good ? "var(--green)" : "var(--fg-3)", fontWeight: 700, fontSize: 13 }}>{label}</span>;
              })()}
              {s.freeMonthUntil && s.freeMonthUntil > Date.now() && <span className="chip teal" style={{ fontSize: 10.5 }}>1 free month applied</span>}
            </div>
            {comped && (
              <p style={{ fontSize: 14, margin: "4px 0 8px" }}>
                Your plan: <b>{plan.name}</b> (complimentary{compUntil ? " until " + compUntil : ""}). There's nothing to pay — Fence Works has put your account on {plan.name} for free.{compUntil ? " After " + compUntil + " your account goes back to the plan you pay for, or Free." : ""}
              </p>
            )}
            {stripeStillBilling && (
              <p style={{ fontSize: 13, margin: "0 0 8px", color: "var(--amber)" }}>
                You also have a paid {PLAN(sub.stripe.plan).name} subscription with Stripe that is still billing. Cancel it under Manage billing &amp; invoices if you don't need it, or contact us.
              </p>
            )}
            <div className="muted" style={{ fontSize: 13 }}><b style={{ color: "var(--fg-2)" }}>Your plan includes:</b> {plan.features.join(" · ")}</div>
            {(() => {
              // the counted caps, where this plan has one (lib/limits.ts)
              const er = estimateRoom(store), pr = productRoom(store);
              const bits = [Number.isFinite(er.limit) && `${er.used} of ${er.limit} estimates this month`, Number.isFinite(pr.limit) && `${pr.used} of ${pr.limit} products`].filter(Boolean);
              return bits.length > 0 && <div className="muted" style={{ fontSize: 13, marginTop: 6 }}><b style={{ color: "var(--fg-2)" }}>Using:</b> {bits.join(" · ")}</div>;
            })()}
            {/* Free: the badge on what their customers see (server/src/poweredby.js) */}
            {planId === "free" && storeCan(store, "billing") && (
              <div className="muted" style={{ fontSize: 13, marginTop: 6 }}>Your quotes and emails show "Powered by Fence Works". <button type="button" disabled={busy === "starter"} onClick={() => choosePlan("starter")}
                style={{ background: "none", border: 0, padding: 0, font: "inherit", fontWeight: 700, color: "var(--teal)", cursor: "pointer" }}>Upgrade to Starter to remove it.</button></div>
            )}
          </div>

          {/* Usage & credits — meters + add-ons + buy-credit packs */}
          <div className="card pad" style={{ marginBottom: 16 }}>
            <div className="row between" style={{ marginBottom: 12, flexWrap: "wrap", gap: 6 }}>
              <span className="row gap-8 kicker kicker-teal"><Icon name="bolt" size={14} />Usage &amp; credits</span>
              {u.balance > 0 && <span className="chip teal" style={{ fontSize: 10.5 }}>{fmtCredits(u.balance)} top-up credits</span>}
            </div>
            {(checkout === "credits" && creditsAdded) && (
              <div className="row gap-8" style={{ marginBottom: 12, fontSize: 13, color: "var(--green)", fontWeight: 700 }}>
                <Icon name="check" size={14} />Credits added — your top-up balance is updated.
              </div>
            )}
            {(() => {
              const Meter = ({ label, used, total, unit }: any) => {
                const pct = pctUsed(used, total);
                return (
                  <div style={{ marginBottom: 12 }}>
                    <div className="row between" style={{ fontSize: 12.5, marginBottom: 5 }}>
                      <span className="muted">{label}</span>
                      <span className="tnum" style={{ color: "var(--fg-2)", fontWeight: 700 }}>{fmtCredits(used)} / {total ? fmtCredits(total) : "0"}{unit}</span>
                    </div>
                    <div style={{ height: 7, borderRadius: 99, background: "var(--surface-2)", border: "1px solid var(--line)", overflow: "hidden" }}>
                      <div style={{ height: "100%", width: pct + "%", background: pct >= 90 ? "var(--amber)" : "var(--teal)", borderRadius: 99, transition: "width .2s" }} />
                    </div>
                  </div>
                );
              };
              return (
                <>
                  <Meter label="Credits used this month" used={u.credits.used} total={u.credits.total} unit="" />
                  <p className="faint" style={{ fontSize: 11.5, margin: "-6px 0 12px" }}>1 credit = 1 text or 1 AI reply · a property lookup is 3 · an AI render is 10 · emails are free.</p>
                  <Meter label="AI talk-minutes used" used={u.voiceMin.used} total={u.voiceMin.total} unit=" min" />
                </>
              );
            })()}
            {/* Add-ons: only the credit balance — seats are shown accurately on the
                Team tab. (The old "no extra seats" read the LOCAL extraSeats counter,
                which is always 0 in live mode, so it falsely claimed none.) */}
            {u.balance > 0 && (
              <div className="row gap-8" style={{ flexWrap: "wrap", marginTop: 2, marginBottom: 12 }}>
                <span className="muted" style={{ fontSize: 12.5 }}>Add-ons:</span>
                <span className="chip" style={{ fontSize: 10.5 }}>{fmtCredits(u.balance)} credits in balance</span>
              </div>
            )}
            <div className="field-label" style={{ marginBottom: 6 }}>Buy more credits</div>
            <div className="row gap-8" style={{ flexWrap: "wrap" }}>
              {PACKS.map(p => (
                <button key={p.id} className="btn btn-ghost btn-sm" disabled={packBusy === p.id} onClick={() => buyCredits(p)} style={{ flexDirection: "column", alignItems: "flex-start", gap: 1, padding: "8px 12px" }}>
                  <b style={{ fontSize: 13 }}>{packBusy === p.id ? "…" : fmtCredits(p.credits) + " credits"}</b>
                  <span className="muted" style={{ fontSize: 11.5 }}>${p.price}</span>
                </button>
              ))}
            </div>
            <p className="faint" style={{ fontSize: 11.5, margin: "12px 0 0" }}>Trials include 10% of plan credits so you can test AI texting &amp; replies risk-free.</p>
          </div>

          {comped ? (
            <div className="row gap-10" style={{ marginTop: 4, flexWrap: "wrap" }}>
              {stripeStillBilling && <button className="btn btn-ghost" onClick={manageBilling}><Icon name="dollar" size={15} />Manage billing &amp; invoices</button>}
            </div>
          ) : (<>
          <div className="kicker" style={{ margin: "0 0 10px" }}>Change plan</div>
          {planNote && (
            <div className="row gap-8" style={{ marginBottom: 10, fontSize: 13, color: "var(--green)", fontWeight: 700 }}>
              <Icon name="check" size={14} />{planNote}
            </div>
          )}
          {billingErr && !planNote && (
            <div className="muted" style={{ marginBottom: 10, fontSize: 12 }}>Couldn't refresh billing — showing your last known plan.</div>
          )}
          {planErr && (
            <div className="card pad" style={{ marginBottom: 10, borderColor: "var(--amber)", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ flex: 1, minWidth: 200, fontSize: 13 }}><b style={{ color: "var(--amber)" }}>Plan not changed.</b> {planErr}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => { setPlanErr(""); setTab("team"); }}><Icon name="user" size={13} />Manage team</button>
            </div>
          )}
          <div style={{ display: "grid", gap: 10 }}>
            {PLANS.filter(p => p.id !== "free").map(p => (
              <div key={p.id} className={"plan-row" + (p.id === planId ? " current" : "")}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="row gap-8" style={{ alignItems: "baseline", flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 800, fontSize: 16 }}>{p.name}</span>
                    <span className="tnum" style={{ color: "var(--teal)", fontWeight: 700 }}>${founder(p.full)}/mo</span>
                    <span className="muted" style={{ fontSize: 12, textDecoration: "line-through" }}>${p.full}</span>
                    {p.popular && <span className="chip teal" style={{ fontSize: 10 }}>Most popular</span>}
                  </div>
                  <div className="muted" style={{ fontSize: 12.5, marginTop: 3 }}>{p.features.slice(0, 4).join(" · ")}</div>
                </div>
                {p.id === planId
                  ? <span className="chip" style={{ flexShrink: 0 }}>Current</span>
                  : <button className="btn btn-primary btn-sm" style={{ flexShrink: 0 }} disabled={busy === p.id} onClick={() => choosePlan(p.id)}>{busy === p.id ? "…" : (PLANS.findIndex(x => x.id === p.id) > PLANS.findIndex(x => x.id === planId) ? "Upgrade" : "Switch")}</button>}
              </div>
            ))}
          </div>

          <p className="faint" style={{ fontSize: 11.5, margin: "14px 0 0" }}>Founder pricing (50% off) auto-applies through {FOUNDER_ENDS_LABEL}. Plan changes take effect right away, up or down, and nothing is charged on the spot: moving up adds the difference for the rest of this billing period to your next invoice, and moving down credits the unused part of your current plan to it. On a free trial, switching costs nothing until the trial ends. Add-on seats carry over, except any the new plan already includes, and you can't switch to fewer seats than your team is using. To leave paid plans, use Cancel subscription.</p>
          <div className="row gap-10" style={{ marginTop: 14, flexWrap: "wrap" }}>
            <button className="btn btn-ghost" onClick={manageBilling}><Icon name="dollar" size={15} />Manage billing &amp; invoices</button>
            {planId !== "free" && <button className="btn btn-sm" style={{ background: "transparent", color: "var(--fg-3)", border: "1px solid var(--line-2)" }} onClick={openCancel}>Cancel subscription</button>}
          </div>
          </>)}
        </>
      )}

      {tab === "profile" && <DeleteAccountCard store={store} />}
      {tab === "profile" && (
        <div className="card pad" style={{ maxWidth: 520 }}>
          <div className="row gap-12" style={{ marginBottom: 16 }}>
            <div className="cust-ava lg">{(name || "U").charAt(0).toUpperCase()}</div>
            <div><div style={{ fontWeight: 700, fontSize: 16 }}>{name}</div><div className="muted" style={{ fontSize: 13 }}>{email}</div></div>
          </div>
          <div style={{ display: "grid", gap: 12 }}>
            <div><label className="field-label">Full name</label><input className="input" defaultValue={name} onBlur={e => store.setSettings({ companyOwner: e.target.value })} /></div>
            <div><label className="field-label">Email</label><input className="input" defaultValue={email} onBlur={e => store.setSettings({ ownerEmail: e.target.value })} /></div>
            <div className="row gap-8" style={{ marginTop: 4 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => go && go("settings", { section: "company" })}><Icon name="building" size={13} />Company settings</button>
              {auth && auth.signOut && <button className="btn btn-sm" style={{ background: "var(--red-tint)", color: "var(--red)" }} onClick={() => auth.signOut()}><Icon name="x" size={13} />Sign out</button>}
            </div>
          </div>
        </div>
      )}

      {tab === "billing" && (
        <div className="card pad">
          <div className="kicker" style={{ marginBottom: 12 }}>Payment history</div>
          {liveBilling && invoicesErr && (
            <div className="row between" style={{ padding: "11px 13px", marginBottom: 8, background: "var(--surface-2)", border: "1px solid var(--line)", borderRadius: "var(--r-md)" }}>
              <div className="muted" style={{ fontSize: 13 }}>Couldn't load invoices.</div>
              <button className="btn btn-ghost btn-sm" onClick={() => setInvoicesTry((n: number) => n + 1)}>Retry</button>
            </div>
          )}
          {invoices.length === 0 && !(liveBilling && invoicesErr)
            ? <EmptyState icon="dollar" title={liveBilling && liveInvoices == null ? "Loading invoices…" : "No invoices yet"} sub={liveBilling ? "Invoices appear here after your first Stripe charge." : "You're on the Free plan. Upgrade to start a subscription."} />
            : <div style={{ display: "grid", gap: 8 }}>
                {invoices.map((iv, i) => (
                  <div key={i} className="row between inv-row" style={{ padding: "11px 13px", background: "var(--surface-2)", border: "1px solid var(--line)", borderRadius: "var(--r-md)" }}>
                    <div className="inv-desc" style={{ minWidth: 0 }}><div style={{ fontWeight: 600, fontSize: 13.5 }}>{iv.desc}</div><div className="muted" style={{ fontSize: 12 }}>{iv.date}</div></div>
                    <div className="row gap-12">
                      <span className="chip green" style={{ fontSize: 11 }}>{iv.status}</span>
                      <span className="display tnum" style={{ fontSize: 15 }}>${iv.amount}.00</span>
                      {iv.url
                        ? <a className="btn btn-ghost btn-sm" title="View invoice (Stripe)" href={iv.url} target="_blank" rel="noopener"><Icon name="download" size={13} /></a>
                        : <button className="btn btn-ghost btn-sm" title="Download receipt (PDF)" onClick={() => printReceipt(iv, s, name)}><Icon name="download" size={13} /></button>}
                    </div>
                  </div>
                ))}
              </div>}
          <p className="faint" style={{ fontSize: 11.5, marginTop: 12 }}>Billing is handled securely by Stripe. Receipts download once a live subscription is connected.</p>
        </div>
      )}

      {cancelling && ReactDOM.createPortal(
        <div className="hub-preview-overlay" data-dialog onClick={() => setCancelling(false)}>
          <div className="hub-quote-modal" onClick={e => e.stopPropagation()}>
            <div className="hub-preview-bar"><span className="row gap-8" style={{ fontSize: 13, fontWeight: 700 }}><Icon name="bolt" size={14} style={{ color: "var(--amber)" }} />{cancelStep === "offer" && onTrial ? "Your trial" : "Before you go…"}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setCancelling(false)}><Icon name="x" size={14} /></button></div>

            {/* STEP 1 — reason */}
            {cancelStep === "reason" && (
              <div className="hub-quote-body" style={{ display: "grid", gap: 12 }}>
                <div style={{ textAlign: "center" }}>
                  <div className="display" style={{ fontSize: 22 }}>Sorry to see you go 👋</div>
                  <p className="muted" style={{ fontSize: 13.5, margin: "6px 0 0" }}>What's the main reason? It helps us improve — and we may have an option that keeps you around.</p>
                </div>
                {/* the mirror: what the software has done for them, and what stops */}
                <RoiPanel store={store} go={go} compact />
                <div style={{ display: "grid", gap: 8 }}>
                  {["Too expensive", "Not using it enough", "Missing a feature I need", "Just testing it out", "Switching to another tool", "Other"].map((r) => (
                    <button key={r} className="btn btn-ghost btn-block" style={{ justifyContent: "space-between" }} onClick={() => pickReason(r)}>
                      <span>{r}</span><Icon name="arrow" size={13} />
                    </button>
                  ))}
                </div>
                <div>
                  <input className="input" value={cancelOther} placeholder="Anything else? (optional)" onChange={e => setCancelOther(e.target.value)} />
                </div>
                <button className="btn btn-block" style={{ background: "transparent", color: "var(--fg-3)", border: "1px solid var(--line-2)" }} onClick={() => setCancelling(false)}>Never mind, keep my plan</button>
              </div>
            )}

            {/* STEP 2 — branched follow-up (tailored to the reason) */}
            {cancelStep === "followup" && (() => {
              // Per-reason follow-up: a warm question + either selectable options
              // (→ cancelFollowup) and/or a verbatim free-text (→ cancelDetail).
              // The most valuable answers (feature ask / "what's better" / other)
              // are textareas captured verbatim.
              const FOLLOWUPS: any = {
                "Too expensive": { q: "What would have worked?", options: ["A lower-priced plan", "Annual billing for a discount", "Fewer credits / pay-as-you-go", "Honestly, not worth it yet"] },
                "Not using it enough": { q: "What got in the way?", options: ["Haven't had time to set it up", "Didn't see results yet", "Setup was confusing", "Slow season — not enough jobs right now"] },
                "Missing a feature I need": { q: "Which feature would've kept you?", textarea: "The feature you needed…", textareaOnly: true },
                "Just testing it out": { q: "What would help you decide?", options: ["Just exploring for now", "Waiting for my busy season", "Comparing a few options", "Need to run it on a real job first"] },
                "Switching to another tool": { q: "Which tool — and what does it do better?", options: ["Jobber", "Housecall Pro", "ServiceTitan", "Paper / spreadsheets", "Other"], textarea: "What does it do better?" },
                "Other": { q: "Tell us what happened", textarea: "What happened?", textareaOnly: true },
              };
              const cfg = FOLLOWUPS[cancelReason] || FOLLOWUPS["Other"];
              // Continue is enabled once they've given us SOMETHING for this reason
              // (an option pick or some free-text) — but never hard-blocks leaving.
              const hasAnswer = !!(cancelFollowup || (cancelDetail && cancelDetail.trim()));
              return (
                <div className="hub-quote-body" style={{ display: "grid", gap: 12 }}>
                  <div style={{ textAlign: "center" }}>
                    <div className="display" style={{ fontSize: 21 }}>{cfg.q}</div>
                    <p className="muted" style={{ fontSize: 13, margin: "6px 0 0" }}>This is just for us — it helps the next contractor like you.</p>
                  </div>
                  {cfg.options && (
                    <div style={{ display: "grid", gap: 8 }}>
                      {cfg.options.map((o: string) => (
                        <button key={o} className={"btn btn-block" + (cancelFollowup === o ? " btn-primary" : " btn-ghost")} style={{ justifyContent: "space-between" }} onClick={() => setCancelFollowup(o)}>
                          <span>{o}</span>{cancelFollowup === o && <Icon name="check" size={13} />}
                        </button>
                      ))}
                    </div>
                  )}
                  {cfg.textarea && (
                    <textarea className="input" rows={cfg.textareaOnly ? 4 : 3} value={cancelDetail} placeholder={cfg.textarea} onChange={e => setCancelDetail(e.target.value)} style={{ resize: "vertical", minHeight: cfg.textareaOnly ? 92 : 72 }} />
                  )}
                  <button className="btn btn-primary btn-block" onClick={() => setCancelStep("offer")}>{hasAnswer ? "Continue" : "Skip"}<Icon name="arrow" size={14} /></button>
                  <button className="btn btn-block" style={{ background: "transparent", color: "var(--fg-3)", border: "1px solid var(--line-2)" }} onClick={() => setCancelStep("reason")}><Icon name="arrow" size={13} style={{ transform: "rotate(180deg)" }} />Back</button>
                </div>
              );
            })()}

            {/* STEP 3 — tailored offer */}
            {cancelStep === "offer" && onTrial && (
              <div className="hub-quote-body" style={{ display: "grid", gap: 14 }}>
                {extendDone ? (
                  <div style={{ textAlign: "center", padding: "8px 0" }}>
                    <div className="display" style={{ fontSize: 22, color: "var(--green)" }}>{extendDone}</div>
                    <p className="muted" style={{ fontSize: 13.5, margin: "6px 0 0" }}>Take your time — you're all set.</p>
                  </div>
                ) : (
                  <>
                    <div style={{ textAlign: "center" }}>
                      <div className="display" style={{ fontSize: 22 }}>{trialExtended ? "Not ready to commit?" : "Need more time? 🙌"}</div>
                      <p className="muted" style={{ fontSize: 13.5, margin: "6px 0 0" }}>Your <b style={{ color: "var(--fg)" }}>founder rate (50% off)</b> stays reserved for whenever you decide to subscribe — and your data stays safe if you switch to Free.</p>
                    </div>
                    {trialExtended ? (
                      <div className="card pad" style={{ textAlign: "center", background: "var(--surface-2)" }}>
                        <span className="row gap-8" style={{ justifyContent: "center", fontSize: 13, fontWeight: 700, color: "var(--fg-2)" }}><Icon name="check" size={14} style={{ color: "var(--green)" }} />Trial already extended</span>
                      </div>
                    ) : (
                      <button className="btn btn-primary btn-block" disabled={extendBusy} onClick={extendTrial}><Icon name="check" size={15} />{extendBusy ? "Extending…" : "Extend my trial 14 days"}</button>
                    )}
                    <button className="btn btn-block" style={{ background: "var(--red-tint)", color: "var(--red)", border: "1px solid var(--red-tint)" }} onClick={confirmCancel}>End trial — switch to Free</button>
                    <p className="faint" style={{ fontSize: 11, textAlign: "center", margin: 0 }}>No charge while you're on a trial. Your founder discount is locked in until you subscribe (founder pricing ends {FOUNDER_ENDS_LABEL}).</p>
                  </>
                )}
              </div>
            )}

            {cancelStep === "offer" && !onTrial && (
              <div className="hub-quote-body" style={{ display: "grid", gap: 14 }}>
                <div style={{ textAlign: "center" }}>
                  <div className="display" style={{ fontSize: 22 }}>Stay and get a month on us 🎁</div>
                  <p className="muted" style={{ fontSize: 13.5, margin: "6px 0 0" }}>You're on <b style={{ color: "var(--fg)" }}>{plan.name}</b> at the founder rate (50% off). Cancel and you'll lose that locked-in price forever.{cancelReason === "Too expensive" && cheaperPlan ? " A cheaper plan keeps the discount — switch instead?" : ""}</p>
                </div>
                {!s.saveOfferUsed && (
                  <button className="btn btn-primary btn-block" onClick={applySaveOffer}><Icon name="check" size={15} />Keep my plan — give me 1 free month</button>
                )}
                {/* Starter has no cheaper PAID plan — leaving it is Cancel, below
                    (asking the server to "switch to free" only ever errored).
                    The downgrade is recorded only if it actually happened: the
                    confirm can be declined, and a team too big for the smaller
                    plan is refused. */}
                {cheaperPlan && (
                  <button className={"btn btn-block" + (cancelReason === "Too expensive" ? " btn-primary" : " btn-ghost")} onClick={async () => { setCancelling(false); if (await choosePlan(cheaperPlan)) submitFeedback("downgraded"); }}>
                    <Icon name="arrow" size={14} style={{ transform: "rotate(90deg)" }} />Downgrade to {PLAN(cheaperPlan).name} instead
                  </button>
                )}
                <button className="btn btn-block" style={{ background: "var(--red-tint)", color: "var(--red)", border: "1px solid var(--red-tint)" }} onClick={confirmCancel}>Cancel anyway — move to Free</button>
                <p className="faint" style={{ fontSize: 11, textAlign: "center", margin: 0 }}>Your data stays safe on Free. You can re-subscribe anytime (founder rate ends {FOUNDER_ENDS_LABEL}).</p>
              </div>
            )}
          </div>
        </div>, document.body)}
    </div>
  );
}

Object.assign(window, { AccountBilling, PLANS, PLAN, founder, FOUNDER_ENDS, FOUNDER_ENDS_LABEL, founderActive });
export { printReceipt, AccountBilling, FOUNDER_OFF, FOUNDER_ENDS, FOUNDER_ENDS_LABEL, founderActive, founder, PLANS, PLAN, productRoom, estimateRoom, limitPrompt, LimitNote };
